import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import adTestingService from '../services/adTesting.service';
const initialState = {
    funnel: null,
    funnelLoading: false,
    funnelError: null,
    monthlyBudget: null,
    monthlyBudgetLoading: false,
    monthlyBudgetError: null,
    savingBudget: false,
    benchmarkHistory: null,
    benchmarkHistoryLoading: false,
    benchmarkHistoryError: null,
    adDayByDay: null,
    adDayByDayLoading: false,
    adDayByDayError: null,
    refreshingBenchmark: false,
    refreshingData: false,
    tests: null,
    testsLoading: false,
    testsError: null,
    lockingId: null,
    adPreview: null,
    adPreviewLoading: false,
    adPreviewError: null,
    adAudience: null,
    adAudienceLoading: false,
    adAudienceError: null,
};
// ── Thunks ─────────────────────────────────────────────────────────────────
export const fetchFunnelSpend = createAsyncThunk('adTesting/fetchFunnelSpend', async (params, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await adTestingService.getFunnelSpend(params, token || '');
        if (response?.data?.success) {
            return response.data.data;
        }
        return rejectWithValue(response?.data?.message || 'Failed to fetch funnel spend');
    }
    catch (err) {
        return rejectWithValue(err?.message || 'Failed to fetch funnel spend');
    }
});
export const fetchMonthlyBudget = createAsyncThunk('adTesting/fetchMonthlyBudget', async (params, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await adTestingService.getMonthlyBudget(params, token || '');
        if (response?.data?.success) {
            return response.data.data;
        }
        return rejectWithValue(response?.data?.message || 'Failed to fetch monthly budget');
    }
    catch (err) {
        return rejectWithValue(err?.message || 'Failed to fetch monthly budget');
    }
});
export const saveMonthlyBudget = createAsyncThunk('adTesting/saveMonthlyBudget', async (payload, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await adTestingService.saveMonthlyBudget(payload, token || '');
        if (response?.data?.success) {
            return response.data.data;
        }
        return rejectWithValue(response?.data?.message || 'Failed to save monthly budget');
    }
    catch (err) {
        return rejectWithValue(err?.message || 'Failed to save monthly budget');
    }
});
export const fetchBenchmarkHistory = createAsyncThunk('adTesting/fetchBenchmarkHistory', async (params, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await adTestingService.getBenchmarkHistory(params, token || '');
        if (response?.data?.success) {
            return response.data.data;
        }
        return rejectWithValue(response?.data?.message || 'Failed to fetch benchmark history');
    }
    catch (err) {
        return rejectWithValue(err?.message || 'Failed to fetch benchmark history');
    }
});
export const fetchAdDayByDay = createAsyncThunk('adTesting/fetchAdDayByDay', async (params, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await adTestingService.getAdDayByDay(params, token || '');
        if (response?.data?.success) {
            return response.data.data;
        }
        return rejectWithValue(response?.data?.message || 'Failed to fetch day-by-day comparison');
    }
    catch (err) {
        return rejectWithValue(err?.message || 'Failed to fetch day-by-day comparison');
    }
});
export const refreshBenchmark = createAsyncThunk('adTesting/refreshBenchmark', async (payload, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await adTestingService.refreshBenchmark(payload, token || '');
        if (response?.data?.success)
            return true;
        return rejectWithValue(response?.data?.message || 'Failed to refresh benchmark');
    }
    catch (err) {
        return rejectWithValue(err?.message || 'Failed to refresh benchmark');
    }
});
// Fire-and-forget: enqueues the background Meta sync + test rebuild. Resolves as soon
// as the job is accepted (message shown via toast); the data updates minutes later.
export const refreshData = createAsyncThunk('adTesting/refreshData', async (payload, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await adTestingService.refreshData(payload, token || '');
        if (response?.data?.success)
            return String(response?.data?.message || 'Refresh started.');
        return rejectWithValue(response?.data?.message || 'Failed to start refresh');
    }
    catch (err) {
        return rejectWithValue(err?.message || 'Failed to start refresh');
    }
});
// Poll target for the refresh loader — resolves to whether the refresh job is still
// running for the client. Errors resolve to `false` so a flaky poll ends the loader
// (rather than spinning forever).
export const fetchRefreshStatus = createAsyncThunk('adTesting/fetchRefreshStatus', async (payload, { getState }) => {
    try {
        const token = getState().auth?.token;
        const response = await adTestingService.getRefreshStatus(payload, token || '');
        return !!response?.data?.data?.in_flight;
    }
    catch {
        return false;
    }
});
export const fetchTests = createAsyncThunk('adTesting/fetchTests', async (params, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await adTestingService.getTests(params, token || '');
        if (response?.data?.success) {
            return response.data.data;
        }
        return rejectWithValue(response?.data?.message || 'Failed to fetch ad tests');
    }
    catch (err) {
        return rejectWithValue(err?.message || 'Failed to fetch ad tests');
    }
});
export const fetchAdPreview = createAsyncThunk('adTesting/fetchAdPreview', async (params, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await adTestingService.getAdPreview(params, token || '');
        if (response?.data?.success) {
            return response.data.data;
        }
        return rejectWithValue(response?.data?.message || 'Preview not available');
    }
    catch (err) {
        return rejectWithValue(err?.message || 'Preview not available');
    }
});
export const fetchAdAudience = createAsyncThunk('adTesting/fetchAdAudience', async (params, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await adTestingService.getAdAudience(params, token || '');
        if (response?.data?.success) {
            return response.data.data;
        }
        return rejectWithValue(response?.data?.message || 'Audience not available');
    }
    catch (err) {
        return rejectWithValue(err?.message || 'Audience not available');
    }
});
export const lockTest = createAsyncThunk('adTesting/lockTest', async (payload, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await adTestingService.lockTest(payload, token || '');
        if (response?.data?.success) {
            return response.data.data;
        }
        return rejectWithValue(response?.data?.message || 'Failed to update test lock');
    }
    catch (err) {
        return rejectWithValue(err?.message || 'Failed to update test lock');
    }
});
// ── Slice ──────────────────────────────────────────────────────────────────
const adTestingSlice = createSlice({
    name: 'adTesting',
    initialState,
    reducers: {
        clearFunnel(state) {
            state.funnel = null;
            state.funnelError = null;
        },
        clearMonthlyBudget(state) {
            state.monthlyBudget = null;
            state.monthlyBudgetError = null;
        },
        clearAdDayByDay(state) {
            state.adDayByDay = null;
            state.adDayByDayError = null;
        },
        clearBenchmarkHistory(state) {
            state.benchmarkHistory = null;
            state.benchmarkHistoryError = null;
        },
        clearTests(state) {
            state.tests = null;
            state.testsError = null;
        },
        clearAdPreview(state) {
            state.adPreview = null;
            state.adPreviewError = null;
        },
        clearAdAudience(state) {
            state.adAudience = null;
            state.adAudienceError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchFunnelSpend.pending, (state) => {
            state.funnelLoading = true;
            state.funnelError = null;
        })
            .addCase(fetchFunnelSpend.fulfilled, (state, action) => {
            state.funnelLoading = false;
            state.funnel = action.payload;
        })
            .addCase(fetchFunnelSpend.rejected, (state, action) => {
            state.funnelLoading = false;
            state.funnel = null;
            state.funnelError = action.payload || action.error.message || 'Failed to fetch funnel spend';
        })
            .addCase(fetchMonthlyBudget.pending, (state) => {
            state.monthlyBudgetLoading = true;
            state.monthlyBudgetError = null;
        })
            .addCase(fetchMonthlyBudget.fulfilled, (state, action) => {
            state.monthlyBudgetLoading = false;
            state.monthlyBudget = action.payload;
        })
            .addCase(fetchMonthlyBudget.rejected, (state, action) => {
            state.monthlyBudgetLoading = false;
            state.monthlyBudget = null;
            state.monthlyBudgetError = action.payload || action.error.message || 'Failed to fetch monthly budget';
        })
            .addCase(saveMonthlyBudget.pending, (state) => {
            state.savingBudget = true;
        })
            .addCase(saveMonthlyBudget.fulfilled, (state, action) => {
            state.savingBudget = false;
            state.monthlyBudget = action.payload;
        })
            .addCase(saveMonthlyBudget.rejected, (state, action) => {
            state.savingBudget = false;
            state.monthlyBudgetError = action.payload || action.error.message || 'Failed to save monthly budget';
        })
            .addCase(fetchBenchmarkHistory.pending, (state) => {
            state.benchmarkHistoryLoading = true;
            state.benchmarkHistoryError = null;
        })
            .addCase(fetchBenchmarkHistory.fulfilled, (state, action) => {
            state.benchmarkHistoryLoading = false;
            state.benchmarkHistory = action.payload;
        })
            .addCase(fetchBenchmarkHistory.rejected, (state, action) => {
            state.benchmarkHistoryLoading = false;
            state.benchmarkHistory = null;
            state.benchmarkHistoryError = action.payload || action.error.message || 'Failed to fetch benchmark history';
        })
            .addCase(fetchAdDayByDay.pending, (state) => {
            state.adDayByDayLoading = true;
            state.adDayByDayError = null;
        })
            .addCase(fetchAdDayByDay.fulfilled, (state, action) => {
            state.adDayByDayLoading = false;
            state.adDayByDay = action.payload;
        })
            .addCase(fetchAdDayByDay.rejected, (state, action) => {
            state.adDayByDayLoading = false;
            state.adDayByDay = null;
            state.adDayByDayError = action.payload || action.error.message || 'Failed to fetch day-by-day comparison';
        })
            .addCase(refreshBenchmark.pending, (state) => {
            state.refreshingBenchmark = true;
        })
            .addCase(refreshBenchmark.fulfilled, (state) => {
            state.refreshingBenchmark = false;
        })
            .addCase(refreshBenchmark.rejected, (state) => {
            state.refreshingBenchmark = false;
        })
            .addCase(refreshData.pending, (state) => {
            state.refreshingData = true;
        })
            .addCase(refreshData.fulfilled, (state) => {
            state.refreshingData = false;
        })
            .addCase(refreshData.rejected, (state) => {
            state.refreshingData = false;
        })
            .addCase(fetchTests.pending, (state) => {
            state.testsLoading = true;
            state.testsError = null;
        })
            .addCase(fetchTests.fulfilled, (state, action) => {
            state.testsLoading = false;
            state.tests = action.payload;
        })
            .addCase(fetchTests.rejected, (state, action) => {
            state.testsLoading = false;
            state.tests = null;
            state.testsError = action.payload || action.error.message || 'Failed to fetch ad tests';
        })
            .addCase(lockTest.pending, (state, action) => {
            state.lockingId = action.meta.arg.id;
        })
            .addCase(lockTest.fulfilled, (state, action) => {
            state.lockingId = null;
            const rows = state.tests?.rows;
            if (rows) {
                const i = rows.findIndex((r) => r._id === action.payload._id);
                if (i >= 0)
                    rows[i] = action.payload;
            }
        })
            .addCase(lockTest.rejected, (state) => {
            state.lockingId = null;
        })
            .addCase(fetchAdPreview.pending, (state) => {
            state.adPreviewLoading = true;
            state.adPreviewError = null;
            state.adPreview = null;
        })
            .addCase(fetchAdPreview.fulfilled, (state, action) => {
            state.adPreviewLoading = false;
            state.adPreview = action.payload;
        })
            .addCase(fetchAdPreview.rejected, (state, action) => {
            state.adPreviewLoading = false;
            state.adPreview = null;
            state.adPreviewError = action.payload || action.error.message || 'Preview not available';
        })
            .addCase(fetchAdAudience.pending, (state) => {
            state.adAudienceLoading = true;
            state.adAudienceError = null;
        })
            .addCase(fetchAdAudience.fulfilled, (state, action) => {
            state.adAudienceLoading = false;
            state.adAudience = action.payload;
        })
            .addCase(fetchAdAudience.rejected, (state, action) => {
            state.adAudienceLoading = false;
            state.adAudience = null;
            state.adAudienceError = action.payload || action.error.message || 'Audience not available';
        });
    },
});
export const { clearFunnel, clearMonthlyBudget, clearBenchmarkHistory, clearAdDayByDay, clearTests, clearAdPreview, clearAdAudience } = adTestingSlice.actions;
// ── Selectors ──────────────────────────────────────────────────────────────
export const selectFunnel = (s) => s.adTesting?.funnel || null;
export const selectFunnelBoxes = (s) => s.adTesting?.funnel?.boxes || [];
export const selectFunnelLoading = (s) => !!s.adTesting?.funnelLoading;
export const selectFunnelError = (s) => s.adTesting?.funnelError || null;
export const selectMonthlyBudget = (s) => s.adTesting?.monthlyBudget || null;
export const selectMonthlyBudgetLoading = (s) => !!s.adTesting?.monthlyBudgetLoading;
export const selectMonthlyBudgetError = (s) => s.adTesting?.monthlyBudgetError || null;
export const selectSavingBudget = (s) => !!s.adTesting?.savingBudget;
export const selectBenchmarkHistory = (s) => s.adTesting?.benchmarkHistory || null;
export const selectBenchmarkHistoryLoading = (s) => !!s.adTesting?.benchmarkHistoryLoading;
export const selectBenchmarkHistoryError = (s) => s.adTesting?.benchmarkHistoryError || null;
export const selectAdDayByDay = (s) => s.adTesting?.adDayByDay || null;
export const selectAdDayByDayLoading = (s) => !!s.adTesting?.adDayByDayLoading;
export const selectAdDayByDayError = (s) => s.adTesting?.adDayByDayError || null;
export const selectRefreshingBenchmark = (s) => !!s.adTesting?.refreshingBenchmark;
export const selectRefreshingData = (s) => !!s.adTesting?.refreshingData;
export const selectTests = (s) => s.adTesting?.tests || null;
export const selectTestsLoading = (s) => !!s.adTesting?.testsLoading;
export const selectTestsError = (s) => s.adTesting?.testsError || null;
export const selectLockingId = (s) => s.adTesting?.lockingId || null;
export const selectAdPreview = (s) => s.adTesting?.adPreview || null;
export const selectAdPreviewLoading = (s) => !!s.adTesting?.adPreviewLoading;
export const selectAdPreviewError = (s) => s.adTesting?.adPreviewError || null;
export const selectAdAudience = (s) => s.adTesting?.adAudience || null;
export const selectAdAudienceLoading = (s) => !!s.adTesting?.adAudienceLoading;
export default adTestingSlice.reducer;
